import { detectIncognito } from "detectincognitojs";

import { getDisplayMode } from "~/lib/displayMode";

import type { AnalyticsAdapter } from "../types";

/**
 * MultiAdapter allows multiple analytics adapters to be used simultaneously.
 * All method calls are forwarded to all registered adapters.
 */
export class MultiAdapter implements AnalyticsAdapter {
  private adapters: Array<AnalyticsAdapter>;
  private incognitoDetectionStarted = false;
  private incognitoStatus: Record<string, unknown> = {};

  constructor(adapters: Array<AnalyticsAdapter>) {
    this.adapters = adapters.filter(Boolean); // Remove any null/undefined adapters
  }

  init(): void | Promise<void> {
    const initPromises = this.adapters.map((adapter, index) => {
      try {
        return adapter.init();
      } catch (error) {
        console.error(`Failed to initialize analytics adapter at index ${index}:`, error);
        return undefined;
      }
    });

    // If any adapter returns a promise, we need to handle it
    const hasPromises = initPromises.some((result) => result instanceof Promise);

    if (hasPromises) {
      return Promise.all(initPromises.map((result) => (result instanceof Promise ? result : Promise.resolve())))
        .then(() => undefined)
        .catch((error) => {
          console.error("Error initializing analytics adapters:", error);
        });
    }
  }

  private startIncognitoDetection(): void {
    if (this.incognitoDetectionStarted || typeof window === "undefined") {
      return;
    }

    this.incognitoDetectionStarted = true;

    try {
      void detectIncognito()
        .then((status) => {
          this.incognitoStatus = status;
        })
        .catch(() => undefined);
    } catch {
      // Analytics delivery must not depend on browser capability detection.
    }
  }

  page(input: { name?: string; properties?: Record<string, unknown> }): void {
    this.startIncognitoDetection();
    this.adapters.forEach((adapter, index) => {
      try {
        adapter.page({
          ...input,
          properties: { ...input.properties, displayMode: getDisplayMode(), ...this.incognitoStatus },
        });
      } catch (error) {
        console.error(`Error in analytics adapter at index ${index} page call:`, error);
      }
    });
  }

  track(input: { event: string; properties?: Record<string, unknown> }): void {
    this.startIncognitoDetection();
    this.adapters.forEach((adapter, index) => {
      try {
        void adapter.track({ event: input.event, properties: { ...input.properties, ...this.incognitoStatus } });
      } catch (error) {
        console.error(`Error in analytics adapter at index ${index} track call:`, error);
      }
    });
  }

  identify(input: { userId?: string; traits?: Record<string, unknown> }): void {
    this.adapters.forEach((adapter, index) => {
      try {
        adapter.identify(input);
      } catch (error) {
        console.error(`Error in analytics adapter at index ${index} identify call:`, error);
      }
    });
  }

  reset(): void {
    this.adapters.forEach((adapter, index) => {
      try {
        adapter.reset();
      } catch (error) {
        console.error(`Error in analytics adapter at index ${index} reset call:`, error);
      }
    });
  }
}
